import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Car, Plus, X, AlertCircle, Clock, Users, ShieldAlert, Phone, ArrowLeft, Venus, Mars, UserCheck } from "lucide-react";

function RideCard({ ride }) {
  const isOffer = ride.type === "offer";
  const waMsg = encodeURIComponent(
    isOffer
      ? `Hi! I saw your ride offer on CampusSwap from ${ride.from} to ${ride.to} on ${ride.date} at ${ride.time}. Is there still space?`
      : `Hi! I saw you're looking for a ride from ${ride.from} to ${ride.to} on ${ride.date} on CampusSwap. I can offer a ride!`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start justify-between mb-2">
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isOffer ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
          {isOffer ? "OFFERING RIDE" : "LOOKING FOR RIDE"}
        </span>
        {ride.genderPref && ride.genderPref !== "any" && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${ride.genderPref === "women" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"}`}>
            {ride.genderPref === "women" ? <><Venus size={10} /> Women only</> : <><Mars size={10} /> Men only</>}
          </span>
        )}
        <span className={`text-sm font-bold ${ride.price === 0 ? "text-green-600" : "text-blue-600"}`}>
          {ride.price === 0 ? "FREE" : `₹${ride.price}/seat`}
        </span>
      </div>
      <div className="flex items-center gap-2 my-2">
        <div className="flex-1 text-center">
          <p className="font-bold text-gray-800 text-sm">{ride.from}</p>
          <p className="text-xs text-gray-400">From</p>
        </div>
        <div className="flex flex-col items-center">
          <Car size={18} className="text-blue-500" />
          <div className="h-px w-10 bg-gray-200 mt-1" />
        </div>
        <div className="flex-1 text-center">
          <p className="font-bold text-gray-800 text-sm">{ride.to}</p>
          <p className="text-xs text-gray-400">To</p>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
        <div className="flex items-center gap-1"><Clock size={12} />{ride.date} · {ride.time}</div>
        {isOffer && <div className="flex items-center gap-1"><Users size={12} />{ride.seats} seat{ride.seats > 1 ? "s" : ""}</div>}
      </div>
      <p className="text-xs text-gray-400 mt-1">{ride.postedByName} · {ride.college}</p>
      {ride.note && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{ride.note}</p>}
      <a href={`https://wa.me/91${ride.whatsapp?.replace(/\D/g, "")}?text=${waMsg}`}
        target="_blank" rel="noreferrer"
        className="mt-3 block text-center bg-green-500 text-white py-2 rounded-xl text-sm font-semibold hover:bg-green-600">
        {isOffer ? "Book Seat" : "Offer Ride"}
      </a>
      {/* SOS Emergency Strip */}
      <div className="mt-2 flex gap-2">
        <a href="tel:112"
          className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 border border-red-200 text-red-600 py-2 rounded-xl text-xs font-bold hover:bg-red-100">
          <Phone size={13} /> Emergency 112
        </a>
        <a href={`https://wa.me/?text=${encodeURIComponent("🚨 SOS! I am in a rideshare and need help. My live location: https://maps.google.com/?q=My+Location")}`}
          target="_blank" rel="noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-600 py-2 rounded-xl text-xs font-bold hover:bg-orange-100">
          <ShieldAlert size={13} /> Share Location
        </a>
      </div>
    </div>
  );
}

export default function Rideshare() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("offer");
  const [showForm, setShowForm] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [genderFilter, setGenderFilter] = useState("all");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    type: "offer", from: "", to: "", date: "", time: "", seats: "1", price: "0", note: "", whatsapp: userProfile?.phone || "", genderPref: "any"
  });

  useEffect(() => { fetchRides(); }, []);

  async function fetchRides() {
    setLoading(true);
    try {
      const snap = await get(ref(db, "rideshares"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setRides(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setRides([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.from.trim() || !form.to.trim()) return setError("Enter from and to locations.");
    if (!form.date.trim() || !form.time.trim()) return setError("Enter date and time.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      await push(ref(db, "rideshares"), {
        type: form.type,
        from: form.from.trim(),
        to: form.to.trim(),
        date: form.date.trim(),
        time: form.time.trim(),
        seats: Number(form.seats) || 1,
        price: Number(form.price) || 0,
        note: form.note.trim(),
        whatsapp: form.whatsapp.trim(),
        genderPref: form.genderPref,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        createdAt: new Date().toISOString(),
      });
      setForm({ type: "offer", from: "", to: "", date: "", time: "", seats: "1", price: "0", note: "", whatsapp: userProfile?.phone || "", genderPref: "any" });
      setShowForm(false);
      fetchRides();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  const filtered = rides.filter(r => r.type === activeTab && (genderFilter === "all" || !r.genderPref || r.genderPref === "any" || r.genderPref === genderFilter));

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-green-600 to-teal-600 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Campus Rideshare</h1>
              <p className="text-green-100 text-xs mt-0.5">Share rides, split costs, go green</p>
            </div>
          </div>
          <button onClick={() => policyAccepted ? setShowForm(true) : setShowPolicy(true)}
            className="bg-white text-green-700 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Post
          </button>
        </div>
      </div>

      {/* Safety Banner */}
      <div className="mx-4 mt-3 mb-1 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-bold text-amber-800">Safety Tips</p>
          <p className="text-xs text-amber-700 mt-0.5">Only share rides with verified campus students. Tell a trusted person your travel plan. In emergency, call <strong>112</strong>. Use the SOS button on any ride card.</p>
        </div>
      </div>

      <div className="px-4 mt-3 mb-3">
        <div className="bg-white rounded-xl shadow-sm p-1 flex">
          <button onClick={() => setActiveTab("offer")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${activeTab === "offer" ? "bg-green-500 text-white" : "text-gray-600"}`}>
            Rides Available ({rides.filter(r => r.type === "offer").length})
          </button>
          <button onClick={() => setActiveTab("request")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${activeTab === "request" ? "bg-blue-500 text-white" : "text-gray-600"}`}>
            Looking for Ride ({rides.filter(r => r.type === "request").length})
          </button>
        </div>
      </div>

      {/* Gender Filter */}
      <div className="px-4 mb-4 flex gap-2">
        {[["all","All Rides","bg-gray-700"],["women","Women Only","bg-pink-500"],["men","Men Only","bg-blue-500"]].map(([val, label, cls]) => (
          <button key={val} onClick={() => setGenderFilter(val)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${genderFilter === val ? `${cls} text-white border-transparent` : "bg-white text-gray-600 border-gray-200"}`}>
            {val === "women" && <Venus size={11} />}{val === "men" && <Mars size={11} />}{val === "all" && <UserCheck size={11} />}
            {label}
          </button>
        ))}
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-36 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Car size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No rides posted yet</p>
            <button onClick={() => policyAccepted ? setShowForm(true) : setShowPolicy(true)} className="mt-4 bg-green-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post a Ride
            </button>
          </div>
        ) : filtered.map(r => <RideCard key={r.id} ride={r} />)}
      </div>

      {/* Rideshare Policy Modal */}
      {showPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Rideshare Safety Policy</h3>
              <button onClick={() => setShowPolicy(false)}><X size={24} /></button>
            </div>
            <div className="p-4 space-y-4 pb-32">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-amber-800 font-bold text-sm mb-1">⚠️ Important Disclaimer</p>
                <p className="text-amber-700 text-xs leading-relaxed">CampusSwap is a peer-to-peer platform. We do not operate, organize, or guarantee any rideshare. By posting or joining a ride, you agree that CampusSwap is not liable for any incidents, accidents, losses, or damages that may occur.</p>
              </div>
              {[
                { icon: "🚗", title: "Platform Role", body: "CampusSwap only connects students. We are not a transport company. All ride arrangements are between users only." },
                { icon: "🔒", title: "Your Safety is Your Responsibility", body: "Always verify the identity of the person you're riding with. Share your travel plan with a trusted friend or family member before every ride." },
                { icon: "📵", title: "No Cash or Advance Payments", body: "Never pay in advance for a ride. All cost-sharing should happen only after the ride is completed safely." },
                { icon: "🚫", title: "Zero Tolerance Policy", body: "Any report of harassment, misconduct, or abuse will result in immediate permanent ban. Use the in-app report feature or email campusswap.official@gmail.com." },
                { icon: "👩", title: "Women-Only Rides", body: "Women-only rides are verified by gender preference. However, always exercise additional caution and share live location with a trusted contact." },
                { icon: "📞", title: "Emergency Protocol", body: "If you feel unsafe at any point, call 112 immediately. Use the SOS button on any ride card to share your location via WhatsApp." },
                { icon: "⚖️", title: "Legal", body: "CampusSwap is not a licensed transport aggregator. Drivers must hold a valid license and insurance. Passengers ride at their own risk." },
              ].map(({ icon, title, body }) => (
                <div key={title} className="flex gap-3">
                  <span className="text-xl flex-shrink-0">{icon}</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
              <button
                onClick={() => { setPolicyAccepted(true); setShowPolicy(false); setShowForm(true); }}
                className="w-full bg-green-500 text-white py-3.5 rounded-xl font-bold text-sm hover:bg-green-600 mt-2">
                I Understand & Agree — Post a Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Post a Ride</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button type="button" onClick={() => setForm({...form, type: "offer"})}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${form.type === "offer" ? "bg-green-500 text-white" : "text-gray-600"}`}>
                  I'm Offering a Ride
                </button>
                <button type="button" onClick={() => setForm({...form, type: "request"})}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold ${form.type === "request" ? "bg-blue-500 text-white" : "text-gray-600"}`}>
                  I Need a Ride
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input name="from" value={form.from} onChange={handleChange} required placeholder="From (e.g. Pune Station)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />
                <input name="to" value={form.to} onChange={handleChange} required placeholder="To (e.g. College Gate)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input name="date" value={form.date} onChange={handleChange} required placeholder="Date (e.g. 20 March)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />
                <input name="time" value={form.time} onChange={handleChange} required placeholder="Time (e.g. 8:30 AM)"
                  className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />
              </div>
              {form.type === "offer" && (
                <div className="grid grid-cols-2 gap-3">
                  <select name="seats" value={form.seats} onChange={handleChange}
                    className="border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400 text-gray-700">
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} Seat{n>1?"s":""}</option>)}
                  </select>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                    <input name="price" type="number" min="0" value={form.price} onChange={handleChange}
                      placeholder="Price (0=Free)"
                      className="w-full border border-gray-200 rounded-xl pl-7 pr-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />
                  </div>
                </div>
              )}
              <textarea name="note" value={form.note} onChange={handleChange} rows={2}
                placeholder="Extra details (pickup point, luggage, etc.)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400 resize-none" />
              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-green-400" />

              {/* Gender Preference */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Gender Preference</p>
                <div className="flex gap-2">
                  {[["any","Any Gender"],["women","Women Only"],["men","Men Only"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setForm({...form, genderPref: val})}
                      className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition ${form.genderPref === val ? "bg-green-500 text-white border-transparent" : "bg-white text-gray-600 border-gray-200"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button type="submit" disabled={posting}
                className="w-full bg-green-500 text-white py-3.5 rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50">
                {posting ? "Posting..." : "Post Ride"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
